import {
  type ActionButtonProps,
  AiLoadingPanel,
  BoundSelectField,
  BoundTextField,
  Button,
  CenteredLayout,
  Css,
  FormSectionLayout,
  InlineFeedbackBanner,
  StaticField,
  Tokens,
  useTestIds,
} from "@homebound/beam";
import { createMaterialPageUrl } from "src/RouteUrls";
import { MaterialCatalog_ItemFragment } from "src/routes/libraries/material-catalog/MaterialCatalog.generated";
import { MaterialImportFormState } from "src/routes/libraries/material-catalog/material-import/materialImportForm";

export type MaterialImportState = "idle" | "processing" | "failed" | "done" | "completed";

export type MaterialImportStepProps = {
  formState: MaterialImportFormState;
  items: MaterialCatalog_ItemFragment[];
  state: MaterialImportState;
  failureReason?: string;
  submitted?: { itemName: string; manufacturerUrl: string };
  failureAction: ActionButtonProps;
};

/**
 * The wizard's first step: hand the agent an item + a product page URL, then wait on it to complete.
 */
export function MaterialImportStep(props: MaterialImportStepProps) {
  const { formState, items, state, failureReason, submitted, failureAction } = props;

  if (state === "processing") {
    // TODO: Update this so the with matches the widths of the FormSectionLayout (Probably a beam update)
    return (
      <CenteredLayout size="sm">
        <AiLoadingPanel title="We're importing your material..." omitBg />
      </CenteredLayout>
    );
  }

  return (
    <FormSectionLayout
      aiMode
      title="Import Material Details"
      description="Select your item type and the manufacturer's URL to import details. You may edit or add to imported content before saving."
      initialFields={
        <div css={Css.df.fdc.gap3.$}>
          {state === "completed" && (
            <InlineFeedbackBanner
              type="warning"
              description="This import has already been completed. Start another below, or find the material in the catalog."
              actions={[failureAction]}
            />
          )}
          {state === "failed" && (
            <InlineFeedbackBanner
              type="error"
              description={failureReason ?? "We couldn't import a material from that URL."}
              actions={[failureAction]}
            />
          )}
          <div css={Css.df.fdc.gap2.$}>
            {state === "done" && submitted ? (
              <>
                <StaticField label="Item Type" value={submitted.itemName} />
                <StaticField label="URL" value={submitted.manufacturerUrl} />
              </>
            ) : (
              <>
                <BoundSelectField label="Item Type" field={formState.itemId} options={items} fullWidth />
                <BoundTextField
                  label="URL"
                  field={formState.manufacturerUrl}
                  placeholder="https://www.example.com/products/..."
                  fullWidth
                />
              </>
            )}
          </div>
          {state !== "done" && <MaterialImportManualEntryFooter />}
        </div>
      }
    />
  );
}

/** The escape hatch out of the AI flow, back to the hand-entry page. */
function MaterialImportManualEntryFooter() {
  const tid = useTestIds({}, "materialImportManualEntry");

  return (
    <div css={Css.df.aic.jcc.gap1.mt6.$} {...tid}>
      <span css={Css.sm.color(Tokens.OnSurface).$}>AI import not working?</span>
      <Button label="Enter Details Manually" variant="text" onClick={createMaterialPageUrl()} />
    </div>
  );
}
